import React, { useEffect, useState } from 'react';
import axios from 'axios';
import DataTable from '../components/DataTable';
import { EditableCell } from '../components/InlineEditTable';
import useSort from '../hooks/useSort';

const API = process.env.REACT_APP_API_URL;

const emptyForm = () => ({ name: '', description: '', default_cost: '', quantity: '' });

export default function Products() {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(emptyForm());

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = () => axios.get(`${API}/products/`).then(r => setProducts(r.data));

  // The top form only adds now — existing rows are edited in place below.
  const handleSubmit = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/products/`, {
      ...form,
      default_cost: form.default_cost ? parseFloat(form.default_cost) : null,
      quantity: form.quantity ? parseInt(form.quantity) : null,
    });
    setForm(emptyForm());
    fetchProducts();
  };

  // PUT /products/{id} takes the full object (no partial patch) — merge the
  // edited field into the row's current values, then patch state in place.
  const saveField = async (product, patch) => {
    const payload = {
      name: product.name,
      description: product.description || null,
      default_cost: product.default_cost ?? null,
      quantity: product.quantity ?? null,
      ...patch,
    };
    await axios.put(`${API}/products/${product.id}`, payload);
    setProducts(prev => prev.map(p => p.id === product.id ? { ...p, ...patch } : p));
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this product?')) {
      try {
        await axios.delete(`${API}/products/${id}`);
        fetchProducts();
      } catch (err) {
        alert(err.response?.data?.detail || 'Failed to delete product.');
      }
    }
  };

  const { sorted: sortedProducts, field: sortField, dir: sortDir, toggle: handleSort } = useSort(products, null, 'asc');

  const columns = [
    { key: 'name', header: 'Name', width: 150, sortable: true, render: p =>
        <EditableCell value={p.name}
          onSave={v => {
            const name = v.trim();
            if (!name) throw new Error('Name is required');
            return saveField(p, { name });
          }} /> },
    { key: 'description', header: 'Description', width: 260, sortable: true, render: p =>
        <EditableCell value={p.description}
          onSave={v => saveField(p, { description: v.trim() || null })} /> },
    { key: 'quantity', header: 'Quantity', width: 100, sortable: true, align: 'right', render: p =>
        <EditableCell type="number" align="right" value={p.quantity}
          display={p.quantity != null ? p.quantity.toLocaleString() : undefined}
          onSave={v => {
            if (v === '') return saveField(p, { quantity: null });
            const n = parseInt(v);
            if (!(n >= 1)) throw new Error('Quantity must be a whole number of at least 1');
            return saveField(p, { quantity: n });
          }} /> },
    { key: 'default_cost', header: 'Default Cost', width: 130, sortable: true, align: 'right', render: p =>
        <EditableCell type="number" step="0.01" align="right" value={p.default_cost}
          display={p.default_cost != null ? `$${p.default_cost}` : undefined}
          onSave={v => {
            if (v === '') return saveField(p, { default_cost: null });
            const n = parseFloat(v);
            if (Number.isNaN(n)) throw new Error('Default cost must be a number');
            return saveField(p, { default_cost: n });
          }} /> },
    { key: 'actions', header: 'Actions', width: 100, nowrap: true, render: p =>
        <button onClick={() => handleDelete(p.id)} style={{ padding: '4px 12px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Delete</button> },
  ];

  return (
    <div>
      <h2>Products</h2>
      <form onSubmit={handleSubmit} style={{ marginBottom: '24px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input placeholder="Name *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
        <input placeholder="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
        <input placeholder="Default cost" type="number" step="0.01" value={form.default_cost} onChange={e => setForm({ ...form, default_cost: e.target.value })} style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', width: '120px' }} />
        <input placeholder="Quantity" type="number" min="1" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', width: '100px' }} />
        <button type="submit" style={{ padding: '8px 16px', background: '#2c3e50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
          Add Product
        </button>
      </form>
      <DataTable
        rows={sortedProducts}
        columns={columns}
        sort={{ field: sortField, dir: sortDir, onSort: handleSort }}
        emptyText="No products yet."
      />
    </div>
  );
}
